/**
 * EmployeeQuickView — a global HR Management modal showing a colleague's
 * avatar, name, contact details and a couple of at-a-glance KPIs
 * (days to birthday, years of service).
 *
 * Wrap a surface in <EmployeeQuickViewProvider> and call the
 * useEmployeeQuickView() hook to open it for any userId. Used by the staff
 * dashboard team panels and the calendar.
 */

import { createContext, useCallback, useContext, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Modal,
  Stack,
  Group,
  Text,
  Badge,
  Card,
  ThemeIcon,
  Divider,
  Alert,
  Loader,
  Avatar,
  Anchor,
} from '@/components/ui';
import {
  IconAlertCircle,
  IconBriefcase,
  IconCake,
  IconCalendarStats,
  IconMail,
  IconPhone,
} from '@tabler/icons-react';
import { useStaffPortalLookupsService } from '@actschurch/ccms-sdk';
import { fullName, initials, formatDate } from '../utils/hrHelpers';

const EmployeeQuickViewContext = createContext<(userId: string) => void>(() => {});

/** Opens the global employee quick-view modal for a given userId. */
export function useEmployeeQuickView() {
  return useContext(EmployeeQuickViewContext);
}

/**
 * Wraps a surface so any descendant can open the employee quick-view modal
 * via useEmployeeQuickView(). A single modal instance is shared.
 */
export function EmployeeQuickViewProvider({ children }: { children: ReactNode }) {
  const [userId, setUserId] = useState<string | null>(null);
  const open = useCallback((id: string) => { if (id) setUserId(id); }, []);
  return (
    <EmployeeQuickViewContext.Provider value={open}>
      {children}
      <EmployeeQuickViewModal userId={userId} onClose={() => setUserId(null)} />
    </EmployeeQuickViewContext.Provider>
  );
}

function Kpi({ icon, label, value }: { icon: ReactNode; label: string; value: string }) {
  return (
    <Card withBorder padding="sm" radius="md">
      <Group gap="sm" wrap="nowrap">
        <ThemeIcon size="lg" variant="light" radius="md" color="blue">{icon}</ThemeIcon>
        <Stack gap={0} style={{ minWidth: 0 }}>
          <Text size="lg" fw={700}>{value}</Text>
          <Text size="xs" c="dimmed">{label}</Text>
        </Stack>
      </Group>
    </Card>
  );
}

function EmployeeQuickViewModal({ userId, onClose }: { userId: string | null; onClose: () => void }) {
  const lookupsService = useStaffPortalLookupsService();

  const { data, isLoading, error } = useQuery({
    queryKey: ['hr', 'colleague-quick-view', userId],
    queryFn: () => lookupsService.getColleague(userId!),
    enabled: !!userId,
  });

  const name = fullName(data?.firstName, data?.lastName);
  const days = data?.daysUntilBirthday;

  return (
    <Modal opened={!!userId} onClose={onClose} title={<Text fw={600}>Employee</Text>} size="md" centered>
      {isLoading ? (
        <Stack align="center" py="xl" gap="sm">
          <Loader />
          <Text size="sm" c="dimmed">Loading employee…</Text>
        </Stack>
      ) : error || !data ? (
        <Alert color="red" icon={<IconAlertCircle size={16} />}>
          Couldn't load this employee.
        </Alert>
      ) : (
        <Stack gap="md">
          <Group gap="md" wrap="nowrap" align="flex-start">
            <Avatar src={data.avatar ?? undefined} size={72} radius="xl" color="initials">
              {initials(name)}
            </Avatar>
            <Stack gap={4} style={{ minWidth: 0 }}>
              <Text fw={600} size="lg" style={{ wordBreak: 'break-word' }}>{name}</Text>
              {data.jobTitle && (
                <Group gap={6} wrap="nowrap">
                  <IconBriefcase size={14} color="var(--mantine-color-dimmed)" />
                  <Text size="sm" c="dimmed">{data.jobTitle}</Text>
                </Group>
              )}
              {data.typeName && (
                <Badge size="sm" variant="light" w="fit-content">{data.typeName}</Badge>
              )}
            </Stack>
          </Group>

          <Divider />

          {/* Email + phone are quick actions: tap to mail / dial. */}
          <Stack gap={8}>
            <Group gap="xs" wrap="nowrap">
              <IconMail size={15} color="var(--mantine-color-dimmed)" />
              {data.email ? (
                <Anchor href={`mailto:${data.email}`} size="sm" truncate="end">
                  {data.email}
                </Anchor>
              ) : (
                <Text size="sm" c="dimmed">—</Text>
              )}
            </Group>
            <Group gap="xs" wrap="nowrap">
              <IconPhone size={15} color="var(--mantine-color-dimmed)" />
              {data.cellphone ? (
                <Anchor href={`tel:${data.cellphone.replace(/\s+/g, '')}`} size="sm">
                  {data.cellphone}
                </Anchor>
              ) : (
                <Text size="sm" c="dimmed">—</Text>
              )}
            </Group>
          </Stack>

          <Group grow>
            <Kpi
              icon={<IconCake size={18} />}
              label="Days to birthday"
              value={days == null ? '—' : days === 0 ? 'Today' : `${days}`}
            />
            <Kpi
              icon={<IconCalendarStats size={18} />}
              label={data.yearsOfService === 1 ? 'Year of service' : 'Years of service'}
              value={data.yearsOfService == null ? '—' : `${data.yearsOfService}`}
            />
          </Group>

          {data.startDate && (
            <Text size="xs" c="dimmed">Joined {formatDate(data.startDate)}</Text>
          )}
        </Stack>
      )}
    </Modal>
  );
}
